import { z } from 'zod';

/**
 * §15.3.4 — Envanter filtre paneli.
 *
 * Şartnamedeki üç kolonun (Kaynak Mağaza / Genel / Satış Mağazası) **tamamı** burada.
 * Filtreler sunucu tarafında çalışır — 200.000 satır tarayıcıya gelmez (§15.3.4).
 */

/**
 * §15.3.4 — "Her checkbox 3 durumlu olabilir: yok say / olanlar / olmayanlar."
 * Sadece "var" değil "yok" da filtrelenebilmeli.
 */
export const triStateSchema = z.enum(['ANY', 'YES', 'NO']).default('ANY');
export type TriStateFilter = z.infer<typeof triStateSchema>;

const range = () =>
  z
    .object({
      min: z.number().nullable().optional(),
      max: z.number().nullable().optional(),
    })
    .optional();

const intRange = () =>
  z
    .object({
      min: z.number().int().nullable().optional(),
      max: z.number().int().nullable().optional(),
    })
    .optional();

const dateRange = () =>
  z
    .object({
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
    })
    .optional();

// ── Kaynak mağaza (amazon.com) ───────────────────────────────────────────────
export const sourceFiltersSchema = z.object({
  purchasePriceUsd: range(),
  landedCostUsd: range(),
  profitJpy: range(),
  ratingUs: range(),
  reviewCountUs: intRange(),
  salesRankUs: intRange(),
  /**
   * Etkisiz: panelde yok, sorguya girmiyor (bkz. `ETKISIZ_SUZGECLER`). `source_stock_qty` çoğu üründe
   * boş (Keepa adet vermiyor); eski görünümdeki süzgeç listeyi kapatılamaz biçimde daraltıyordu.
   */
  stockQty: intRange(),
  weightLbs: range(),
  shippingCostUsd: range(),

  inStock: triStateSchema,
  hasAmazonSeller: triStateSchema,
  hasFbaSeller: triStateSchema,
  hasPrimeSeller: triStateSchema,
  hasChineseSeller: triStateSchema,

  /** Kaynak verisi kaç saatten eski? (tazelik denetimi — §11.6) */
  staleHoursMin: z.number().int().min(0).nullable().optional(),
  /** Kargo ücreti nereden geldi. */
  shippingSource: z.array(z.enum(['WAREHOUSE', 'FORMULA', 'MANUAL', 'EXCEL', 'NONE'])).optional(),
});
export type SourceFilters = z.infer<typeof sourceFiltersSchema>;

// ── Genel ────────────────────────────────────────────────────────────────────
export const generalFiltersSchema = z.object({
  /** Boş bırakılırsa silinmişler hariç hepsi. */
  status: z
    .array(
      z.enum([
        'ACTIVE',
        'INACTIVE',
        'AWAITING_SHIPPING_COST',
        'PRICING_ERROR',
        'BLOCKED',
        'PENDING_UPLOAD',
      ]),
    )
    .optional(),
  amazonListingStatus: z
    .array(z.enum(['ACTIVE', 'INACTIVE', 'INCOMPLETE', 'SUPPRESSED', 'NOT_FOUND']))
    .optional(),
  /** Kapanma sebebi (`products.status_reason`): bildirim bağlantıları bununla süzüyor. */
  statusReasons: z.array(z.string().max(60)).max(20).optional(),

  hasSalesRank: triStateSchema,
  mainCategories: z.array(z.string()).optional(),
  subCategories: z.array(z.string()).optional(),
  brands: z.array(z.string()).optional(),
  tiers: z.array(z.enum(['HOT', 'WARM', 'NORMAL', 'COLD', 'FROZEN'])).optional(),
  tagIds: z.array(z.string()).optional(),

  /**
   * Etkisiz: panelde yok, sorguya girmiyor (bkz. `ETKISIZ_SUZGECLER`); eski bağlantılar bozulmasın diye duruyor.
   * Prisma `where` ile kurulamıyor: 106 bin ASIN 32.767 bağlı parametreyi aşıyor (P2035, ölçüldü),
   * kelime ve kategori eşleşmesi kodda yapılıyor. Kısıtlamaya takılan ürünler `statusReasons` ile süzülüyor.
   */
  blacklistedBrand: triStateSchema,
  blacklistedAsin: triStateSchema,
  blacklistedKeyword: triStateSchema,
  blacklistedCategory: triStateSchema,

  addedAt: dateRange(),
  updatedAt: dateRange(),

  /** Serbest metin: başlık, ASIN, SKU, marka. */
  search: z.string().max(200).optional(),
});
export type GeneralFilters = z.infer<typeof generalFiltersSchema>;

// ── Satış mağazası (amazon.co.jp) ────────────────────────────────────────────
export const jpFiltersSchema = z.object({
  currentPriceJpy: intRange(),
  profitJpy: intRange(),
  marginPct: range(),
  /** Bizim fiyatımızın Lowest'a göre farkı (%). Negatif = biz daha ucuzuz. */
  lowestDiffPct: range(),
  buyboxDiffPct: range(),
  offerCount: intRange(),
  /** Kendi ilanımızın adedi. */
  currentQty: intRange(),
  salesRankMain: intRange(),
  salesRankSub: intRange(),
  handlingTimeDays: intRange(),
  ratingJp: range(),
  reviewCountJp: intRange(),

  hasPrimeSeller: triStateSchema,
  hasAmazonSeller: triStateSchema,
  hasFbaSeller: triStateSchema,
  hasChineseSeller: triStateSchema,
  /** Etkisiz: panelde yok, sorguya girmiyor (bkz. `ETKISIZ_SUZGECLER`). */
  hasBlacklistedSeller: triStateSchema,

  isOnlySeller: triStateSchema,
  buyboxIsMine: triStateSchema,
  lowestIsMine: triStateSchema,
  /** Fiyatı min fiyata inmiş ürünler — rekabet baskısı göstergesi (§15.2). */
  atMinPrice: triStateSchema,
  atMaxPrice: triStateSchema,
  /**
   * Fiyatı taban fiyatın ALTINDA kalan ürünler — para uyarısı.
   *
   * `atMinPrice` eşitliği de sayıyor; bu süzgeç yalnızca gerçekten altta
   * kalanları veriyor (kaynak fiyatı zıpladı, gönderim Amazon'a oturmadı vb.).
   */
  belowMinPrice: triStateSchema,
  /**
   * Tabana yapışmış ürünler: fiyat taban fiyatta, altında ya da repricer'ın
   * "bu kadarcık fark için fiyata dokunmam" dediği bandın içinde.
   *
   * ⚠️ Band repricer ayarından okunuyor (min_change_jpy / min_change_pct).
   * Sabit eşik yazmak, kur her oynadığında sayının 44 binden 400'e düşmesi
   * demekti (12 Eylül 2026 ölçümü, bkz. göç 20260912010000_taban_farki).
   */
  atFloorPrice: triStateSchema,
});
export type JpFilters = z.infer<typeof jpFiltersSchema>;

// ── Sıralama ─────────────────────────────────────────────────────────────────
export const sortFieldSchema = z.enum([
  'addedAt',
  'updatedAt',
  'asin',
  'sku',
  'titleJp',
  'brand',
  'currentPriceJpy',
  /** Motorun hesapladığı hedef fiyat — kilit açıldığında Amazon'a gidecek olan (ADR-223). */
  'targetPriceJpy',
  'landedCostJpy',
  'minPriceJpy',
  'salesRankMain',
  'salesRankUs',
  'offerCount',
  'orders30d',
  'priorityScore',
  'sourceLastCheckedAt',
  'sourcePriceUsd',
  'handlingTimeDays',
]);
export type SortField = z.infer<typeof sortFieldSchema>;

export const inventorySortSchema = z.object({
  field: sortFieldSchema.default('addedAt'),
  direction: z.enum(['asc', 'desc']).default('desc'),
});
export type InventorySort = z.infer<typeof inventorySortSchema>;

// ── Tam istek ────────────────────────────────────────────────────────────────
export const inventorySearchSchema = z.object({
  source: sourceFiltersSchema.optional(),
  general: generalFiltersSchema.optional(),
  jp: jpFiltersSchema.optional(),
  sort: inventorySortSchema.optional(),
  /** §20.1 / ADR-011 — keyset sayfalama. */
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(250).default(50),
  /** Toplam sayım pahalıdır; yalnızca istendiğinde hesaplanır. */
  withCount: z.boolean().default(false),
});
export type InventorySearchInput = z.infer<typeof inventorySearchSchema>;

/** Boş bir filtre seti — UI'da "Temizle" için. */
export const EMPTY_INVENTORY_FILTERS: InventorySearchInput = {
  limit: 50,
  withCount: false,
};

/**
 * Sorguya girmeyen anahtarlar. Eski bir bağlantıdan gelirse sayılmaz: liste süzülmediği hâlde
 * ekranda "(filtreli)" yazardı.
 */
const ETKISIZ_SUZGECLER: ReadonlySet<string> = new Set([
  'blacklistedBrand',
  'blacklistedAsin',
  'blacklistedKeyword',
  'blacklistedCategory',
  'hasBlacklistedSeller',
  'stockQty',
]);

/**
 * Kaç filtre etkin? Panelde rozet olarak gösterilir.
 *
 * `amazonTaniniyor`: Amazon'un bu pazardaki satıcı kimliği biliniyor mu? Bilinmiyorsa (SA)
 * `jp.hasAmazonSeller` de etkisiz: panelde anahtarı yok, sunucu sorguya katmıyor.
 */
export function countActiveFilters(input: InventorySearchInput, amazonTaniniyor = true): number {
  let count = 0;

  const walk = (value: unknown): void => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      if (value.length > 0) count++;
      return;
    }
    // "Kaynak veri tazeliği" tek sayı; sayılmayınca boş sonuçta "Henüz ürün yok" yazıyordu
    if (typeof value === 'number') {
      count++;
      return;
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      // Aralık nesnesi: min/max veya from/to
      const keys = Object.keys(record);
      const isRange =
        keys.length > 0 && keys.every((k) => ['min', 'max', 'from', 'to'].includes(k));
      if (isRange) {
        if (Object.values(record).some((v) => v !== null && v !== undefined && v !== '')) count++;
        return;
      }
      for (const [anahtar, nested] of Object.entries(record)) {
        if (!ETKISIZ_SUZGECLER.has(anahtar)) walk(nested);
      }
      return;
    }
    if (typeof value === 'string') {
      if (value !== '' && value !== 'ANY') count++;
      return;
    }
  };

  walk(input.source);
  walk(input.general);
  walk(amazonTaniniyor ? input.jp : { ...input.jp, hasAmazonSeller: undefined });
  return count;
}

// ── §15.3.4 Kayıtlı görünümler ──────────────────────────────────────────────

/**
 * "Kayıtlı Görünüm": filtre + kolon seti + sıralama **birlikte** kaydedilir.
 *
 * Kullanıcının 30+ filtre alanı var; "kâr marjı %5'in altındaki, tek satıcılı,
 * kargo ücreti girilmiş ürünler" gibi bir kombinasyonu her seferinde elle kurmak
 * yerine bir kez kaydedip adıyla çağırabilmeli.
 */
export const savedViewSchema = z.object({
  name: z.string().trim().min(1, 'Görünüme bir ad verin').max(60),
  filters: inventorySearchSchema,
  columns: z.array(z.string()).min(1, 'En az bir kolon görünür olmalı').max(60),
  sort: z
    .object({ field: sortFieldSchema, direction: z.enum(['asc', 'desc']) })
    .nullable()
    .optional(),
  /** Paylaşılan görünümü tüm kullanıcılar görür. */
  isShared: z.boolean().default(false),
});
export type SavedViewInput = z.input<typeof savedViewSchema>;
